import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { StatusPill } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Search, Trash2, Pencil, Phone, Mail, MapPin, X, User, CheckCircle2, Download, RefreshCw, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";

const CUSTOMER_TYPES = ["Builder", "Residential", "Body Corporate", "Other"];
const CONTACT_ROLES = ["Site Supervisor", "Project Manager", "Foreman", "Contract Administrator", "Estimator", "Office Administrator", "Other"];
const PAYMENT_TERMS = ["COD", "7 Days", "14 Days", "30 Days"];
const emptyContact = { name: "", position: "", mobile: "", email: "" };

const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const Empty = ({ t }) => <p className="text-slate-400 text-xs px-1 py-1">{t}</p>;
const Section = ({ title, count, open, onToggle, testid, children }) => (
  <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid={testid}>
    <button onClick={onToggle} className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50/60 hover:bg-slate-100 transition-colors" data-testid={`${testid}-toggle`}>
      <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{title} <span className="text-slate-400">({count})</span></span>
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="p-2.5 space-y-2">{children}</div>}
  </div>
);
const empty = { builder: "", name: "", customer_type: "Builder", contacts: [], phone: "", email: "", address: "", suburb: "", state: "", postcode: "", country: "", notes: "",
  billing_company: "", billing_contact: "", billing_address: "", billing_email: "", accounts_email: "", po_number: "", abn: "", payment_terms: "30 Days" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [openSec, setOpenSec] = useState({ active: true, awaiting: true, paid: false, prev: false, chains: true });
  const [busy, setBusy] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const typeFilter = searchParams.get("type") || "";
  const createdFilter = searchParams.get("created") || "";

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    // One-time bulk sync: push current customer names into any stale jobs/projects/invoices
    api.post("/customers/sync-names").catch(() => {});
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addContact = () => setForm((f) => ({ ...f, contacts: [...(f.contacts || []), { ...emptyContact }] }));
  const setContact = (i, k, v) => setForm((f) => { const c = [...(f.contacts || [])]; c[i] = { ...c[i], [k]: v }; return { ...f, contacts: c }; });
  const removeContact = (i) => setForm((f) => ({ ...f, contacts: (f.contacts || []).filter((_, x) => x !== i) }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c, contacts: c.contacts || [] }); setEditId(c.id); setOpen(true); };

  const save = async (opts = {}) => {
    try {
      const payload = { ...form, builder: form.name };
      let cust;
      if (editId) { const r = await api.put(`/customers/${editId}`, payload); cust = r.data; }
      else { const r = await api.post("/customers", payload); cust = r.data; }
      toast.success("Customer saved"); setOpen(false); load();
      if (opts.thenCreateJob && cust) {
        navigate("/jobs", { state: { createJobForCustomer: { id: cust.id, name: cust.name, customer_type: cust.customer_type, phone: cust.phone } } });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete customer?")) return; await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); };
  const showDetail = async (id) => {
    const r = await api.get(`/customers/${id}`); setDetail(r.data); setHistory(null);
    api.get(`/customers/${id}/history`).then((h) => setHistory(h.data)).catch(() => {});
  };
  const reloadHistory = () => { if (detail) api.get(`/customers/${detail.id}/history`).then((h) => setHistory(h.data)).catch(() => {}); };
  const downloadInvoicePdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    }
    catch { toast.error("Could not generate invoice PDF"); }
  };
  const refreshInvoice = async (inv) => {
    setBusy(`r-${inv.id}`);
    try { const r = await api.post(`/invoices/${inv.id}/refresh-xero`); toast.success(r.data?.status === "paid" ? `${inv.invoice_number} is PAID — archived` : "Refreshed from Xero"); reloadHistory(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const sendReminder = async (inv, confirm = false) => {
    setBusy(`m-${inv.id}`);
    try { const r = await api.post(`/invoices/${inv.id}/send-reminder`, confirm ? { confirm: true } : {}); toast.success(r.data.message); reloadHistory(); }
    catch (e) {
      if (e.response?.status === 409 && window.confirm(`${formatApiError(e.response?.data?.detail)}\n\nSend another reminder now?`)) { setBusy(""); return sendReminder(inv, true); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const startOfMonth = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); })();
  const filtered = items.filter((c) => {
    if (typeFilter && c.customer_type !== typeFilter) return false;
    if (createdFilter === "month" && !((c.created_at || "").slice(0, 10) >= startOfMonth)) return false;
    return c.name.toLowerCase().includes(q.toLowerCase()) || (c.builder || "").toLowerCase().includes(q.toLowerCase());
  });
  const filterLabel = typeFilter ? `${typeFilter}s` : createdFilter === "month" ? "New this month" : "";

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Customers</h1><p className="text-slate-500 mt-1">{items.length} customers</p></div>
        <Button data-testid="new-customer-button" onClick={openNew} className="bg-navy hover:bg-navy-light"><Plus className="w-4 h-4 mr-2" />New Customer</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="customers-search" placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        {filterLabel && (
          <span data-testid="customer-filter-banner" className="inline-flex items-center gap-2 bg-navy/5 text-navy border border-navy/10 rounded-full px-3 py-1.5 text-sm font-medium">
            {filterLabel}
            <button data-testid="clear-customer-filter" onClick={() => setSearchParams({})} className="hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`customer-card-${c.id}`} onClick={() => showDetail(c.id)}
            className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-lg text-navy truncate">{c.name}</h3>
                {c.customer_type && <span className="inline-block text-[11px] font-semibold bg-navy/5 text-navy rounded-full px-2 py-0.5 mt-0.5">{c.customer_type}</span>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-customer-${c.id}`} onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-customer-${c.id}`} onClick={(e) => { e.stopPropagation(); del(c.id); }} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
              {c.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{c.phone}</p>}
              {c.email && <p className="flex items-center gap-2 truncate"><Mail className="w-4 h-4 text-slate-400" />{c.email}</p>}
              {c.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{c.address}</p>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">No customers yet.</p>}
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Section 1 — Customer Details */}
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-400">Customer Details</Label>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Customer Type</Label>
                    <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
                      <SelectTrigger data-testid="customer-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{CUSTOMER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Customer Name *</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Main Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
                  <div><Label>Main Email</Label><Input data-testid="customer-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1" /></div>
              </div>
            </div>

            {/* Section 2 — Billing Information */}
            <div className="border-t border-slate-100 pt-4">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-400">Billing Information</Label>
              <p className="text-xs text-slate-400 mb-2">Used exclusively for invoicing & Xero. Billing Email is the default invoice recipient. Billing Company defaults to the Customer Name if left blank.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Billing Company <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="billing-company-input" value={form.billing_company} onChange={(e) => set("billing_company", e.target.value)} placeholder={form.name || "Customer Name"} className="mt-1" /></div>
                  <div><Label>Billing Contact</Label><Input data-testid="billing-contact-input" value={form.billing_contact} onChange={(e) => set("billing_contact", e.target.value)} className="mt-1" /></div>
                </div>
                <div><Label>Billing Address</Label>
                  <AddressAutocomplete testid="billing-address-input" value={form.billing_address}
                    onChange={(v) => set("billing_address", v)}
                    onSelect={(d) => set("billing_address", d.formatted_address || d.street_address)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Billing Email</Label><Input data-testid="billing-email-input" value={form.billing_email} onChange={(e) => set("billing_email", e.target.value)} className="mt-1" /></div>
                  <div><Label>Accounts Email</Label><Input data-testid="accounts-email-input" value={form.accounts_email} onChange={(e) => set("accounts_email", e.target.value)} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>PO Number <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="po-number-input" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} className="mt-1" /></div>
                  <div><Label>ABN <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="abn-input" value={form.abn} onChange={(e) => set("abn", e.target.value)} className="mt-1" /></div>
                  <div><Label>Payment Terms</Label>
                    <Select value={form.payment_terms} onValueChange={(v) => set("payment_terms", v)}>
                      <SelectTrigger data-testid="payment-terms-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAYMENT_TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 — Additional Contacts */}
            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-400 flex items-center gap-1"><User className="w-3.5 h-3.5" />Additional Contacts</Label>
                <button type="button" data-testid="add-contact-button" onClick={addContact} className="text-sm text-blue-700 font-medium flex items-center gap-1"><Plus className="w-4 h-4" />Add contact</button>
              </div>
              <p className="text-xs text-slate-400 mb-2">Site contacts selectable on jobs (kept separate from Billing Contact). Add as many as needed.</p>
              <div className="space-y-2">
                {(form.contacts || []).map((c, i) => (
                  <div key={i} data-testid={`contact-row-${i}`} className="grid grid-cols-2 gap-2 border border-slate-200 rounded-lg p-2 relative">
                    <Input data-testid={`contact-name-${i}`} placeholder="Contact Name" value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} />
                    <Select value={c.position || ""} onValueChange={(v) => setContact(i, "position", v)}>
                      <SelectTrigger data-testid={`contact-position-${i}`}><SelectValue placeholder="Position / Role" /></SelectTrigger>
                      <SelectContent>{CONTACT_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid={`contact-mobile-${i}`} placeholder="Mobile Number" value={c.mobile} onChange={(e) => setContact(i, "mobile", e.target.value)} />
                    <div className="flex gap-1">
                      <Input data-testid={`contact-email-${i}`} placeholder="Email Address" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} className="flex-1" />
                      <button type="button" data-testid={`remove-contact-${i}`} onClick={() => removeContact(i)} className="text-red-500 p-2 hover:bg-red-50 rounded-lg shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                {(form.contacts || []).length === 0 && <p className="text-xs text-slate-400">No additional contacts yet.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="customer-save-button" onClick={() => save()} className="bg-navy hover:bg-navy-light" disabled={!form.name}>Save</Button>
            {!editId && <Button data-testid="customer-save-create-job" onClick={() => save({ thenCreateJob: true })} className="bg-emerald-600 hover:bg-emerald-700" disabled={!form.name}><Plus className="w-4 h-4 mr-1" />Save &amp; Create Job</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl">{detail.name}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm text-slate-600">
                {detail.customer_type && <p className="inline-flex items-center gap-1.5 bg-navy/5 text-navy rounded-full px-3 py-1 text-xs font-semibold">{detail.customer_type}</p>}
                {detail.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{detail.phone}</p>}
                {detail.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{detail.email}</p>}
                {detail.notes && <p className="bg-slate-50 rounded-lg p-3 mt-2">{detail.notes}</p>}
              </div>
              {(detail.contacts || []).length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Additional Contacts ({detail.contacts.length})</h4>
                  <div className="space-y-2">
                    {detail.contacts.map((c, i) => (
                      <div key={i} className="border border-slate-100 rounded-lg p-3 text-sm">
                        <p className="font-medium text-navy">{c.name}{c.position ? <span className="text-slate-400 font-normal"> · {c.position}</span> : ""}</p>
                        <div className="flex flex-wrap gap-x-4 text-slate-500 text-xs mt-0.5">
                          {c.mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.mobile}</span>}
                          {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4">
                  <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Billing Information</h4>
                  <div className="border border-slate-100 rounded-lg p-3 text-sm text-slate-600 space-y-0.5">
                    <p className="font-medium text-navy">{detail.billing_company || detail.name}</p>
                    {detail.billing_contact && <p>{detail.billing_contact}</p>}
                    {detail.billing_address && <p>{detail.billing_address}</p>}
                    {detail.billing_email && <p>{detail.billing_email}</p>}
                    {detail.accounts_email && <p>Accounts: {detail.accounts_email}</p>}
                    <div className="flex flex-wrap gap-x-4 text-xs text-slate-500 pt-1">
                      {detail.abn && <span>ABN: {detail.abn}</span>}
                      {detail.po_number && <span>PO: {detail.po_number}</span>}
                      {detail.payment_terms && <span>Terms: {detail.payment_terms}</span>}
                    </div>
                  </div>
                </div>
              {detail.xero_contact_id && (
                <div className="mt-4" data-testid="customer-xero-block">
                  <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Xero</h4>
                  <div className="border border-blue-100 bg-blue-50/40 rounded-lg p-3 text-sm text-slate-600 space-y-0.5">
                    <p className="flex items-center gap-2 text-emerald-700 font-medium"><CheckCircle2 className="w-4 h-4" />Linked to Xero contact</p>
                    <p className="text-xs">Contact ID: <span className="font-mono text-navy">{detail.xero_contact_id}</span></p>
                    {detail.xero_contact_name && <p className="text-xs">Xero name: {detail.xero_contact_name}</p>}
                    <div className="flex flex-wrap gap-x-4 text-xs text-slate-500 pt-1">
                      {detail.xero_synced_at && <span>Synced: {formatDate(detail.xero_synced_at)}</span>}
                      {detail.xero_updated_at && <span>Last updated: {formatDate(detail.xero_updated_at)}</span>}
                    </div>
                  </div>
                </div>
              )}
              {history && (
                <div className="mt-4 grid grid-cols-3 gap-2" data-testid="customer-summary">
                  {[["Active Jobs", history.summary.active_jobs, "text-navy"],
                    ["Awaiting", history.summary.awaiting_payment, "text-amber-600"],
                    ["Overdue", history.summary.overdue, "text-red-600"],
                    ["Paid Invoices", history.summary.paid_invoices, "text-emerald-600"],
                    ["Outstanding", money(history.summary.total_outstanding), "text-navy"],
                    ["Paid to Date", money(history.summary.total_paid), "text-emerald-600"]].map(([l, v, cls]) => (
                    <div key={l} className="border border-slate-100 rounded-lg p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{l}</p>
                      <p className={`text-lg font-heading font-bold ${cls}`}>{v}</p>
                    </div>
                  ))}
                </div>
              )}

              {history && (
                <div className="mt-4 space-y-3" data-testid="customer-history">
                  <Section title="Active / In Progress Jobs" count={history.active_jobs.length} open={openSec.active} onToggle={() => setOpenSec(s => ({ ...s, active: !s.active }))} testid="sec-active">
                    {history.active_jobs.length === 0 ? <Empty t="No active jobs." /> : history.active_jobs.map((j) => (
                      <div key={j.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-2.5">
                        <div>
                          <p className="text-sm font-medium text-navy">{j.job_number}</p>
                          <p className="text-xs text-slate-500">{j.site_address || j.project_name || "—"} · {formatDate(j.appointment_date)}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusPill status={j.status} />
                          <Link to={`/jobs/${j.id}`} className="text-xs text-navy underline">View</Link>
                        </div>
                      </div>
                    ))}
                  </Section>

                  <Section title="Invoices Awaiting Payment" count={history.awaiting_invoices.length} open={openSec.awaiting} onToggle={() => setOpenSec(s => ({ ...s, awaiting: !s.awaiting }))} testid="sec-awaiting">
                    {history.awaiting_invoices.length === 0 ? <Empty t="No invoices awaiting payment." /> : history.awaiting_invoices.map((inv) => (
                      <div key={inv.id} className="border border-slate-100 rounded-lg p-3" data-testid={`cust-ar-${inv.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-navy">{inv.invoice_number}</span>
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${inv.due_bucket === "overdue" ? "bg-red-100 text-red-700" : inv.due_bucket === "due_soon" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>{inv.due_label}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">Due {formatDate(inv.due_date)} · {inv.site_address || inv.project_name || "—"}</p>
                          </div>
                          <div className="text-right"><p className="font-semibold text-navy">{money(inv.outstanding)}</p><p className="text-[10px] text-slate-400">of {money(inv.total_inc_gst)}</p></div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadInvoicePdf(inv)}><Download className="w-3 h-3 mr-1" />PDF</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => refreshInvoice(inv)} disabled={busy === `r-${inv.id}`}><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => sendReminder(inv)} disabled={busy === `m-${inv.id}`} data-testid={`cust-reminder-${inv.id}`}><Mail className="w-3 h-3 mr-1" />Reminder</Button>
                        </div>
                      </div>
                    ))}
                  </Section>

                  <Section title="Paid Invoices / Archived" count={history.paid_invoices.length} open={openSec.paid} onToggle={() => setOpenSec(s => ({ ...s, paid: !s.paid }))} testid="sec-paid">
                    {history.paid_invoices.length === 0 ? <Empty t="No paid invoices yet." /> : history.paid_invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-2.5">
                        <div>
                          <p className="text-sm font-medium text-navy">{inv.invoice_number} <CheckCircle2 className="inline w-3.5 h-3.5 text-emerald-500" /></p>
                          <p className="text-xs text-slate-500">{inv.status === "credited" ? <span className="text-red-600 font-semibold">Fully Credited{inv.recharged_to ? ` · Recharged as ${inv.recharged_to.invoice_number} to ${inv.recharged_to.customer_name}` : ""}</span> : <>Paid {formatDate(inv.fully_paid_on) || "—"}</>} · {inv.site_address || inv.project_name || "—"}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-emerald-700">{money(inv.amount_paid || inv.total_inc_gst)}</span>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadInvoicePdf(inv)}><Download className="w-3 h-3 mr-1" />PDF</Button>
                        </div>
                      </div>
                    ))}
                  </Section>

                  {(history.job_chains || []).length > 0 && (
                    <Section title="Continuation Timeline" count={history.job_chains.length} open={openSec.chains} onToggle={() => setOpenSec(s => ({ ...s, chains: !s.chains }))} testid="sec-chains">
                      <div className="space-y-4" data-testid="continuation-chains">
                        {history.job_chains.map((chain) => (
                          <div key={chain.root_job_id} className="border border-blue-100 bg-blue-50/40 rounded-lg p-3">
                            {chain.jobs.map((j, idx) => (
                              <div key={j.id} className="relative pl-5">
                                {idx < chain.jobs.length - 1 && <span className="absolute left-[7px] top-4 bottom-0 w-px bg-blue-300" />}
                                <span className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-blue-400 bg-white" />
                                <div className="flex items-center justify-between gap-2 pb-3">
                                  <div>
                                    <p className="text-sm font-medium text-navy flex items-center gap-1.5">
                                      {j.job_number}
                                      {idx === 0 ? <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">Original</span>
                                        : <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Continuation</span>}
                                    </p>
                                    <p className="text-xs text-slate-500">{formatDate(j.appointment_date)} · {j.site_address || "—"}{j.invoice_number ? ` · ${j.invoice_number}` : ""}</p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <StatusPill status={j.status} />
                                    <Link to={`/jobs/${j.id}`} className="text-xs text-navy underline">View</Link>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  <Section title="Previous Jobs History" count={history.previous_jobs.length} open={openSec.prev} onToggle={() => setOpenSec(s => ({ ...s, prev: !s.prev }))} testid="sec-prev">
                    {history.previous_jobs.length === 0 ? <Empty t="No jobs yet." /> : history.previous_jobs.map((j) => (
                      <div key={j.id} className="flex items-center justify-between border border-slate-100 rounded-lg p-2.5">
                        <div><p className="text-sm font-medium text-navy">{j.job_number}</p><p className="text-xs text-slate-500">{formatDate(j.appointment_date)} · {j.site_address || j.project_name || "—"}{j.invoice_number ? ` · ${j.invoice_number}` : ""}</p></div>
                        <div className="flex items-center gap-2"><StatusPill status={j.status} /><Link to={`/jobs/${j.id}`} className="text-xs text-navy underline">View</Link></div>
                      </div>
                    ))}
                  </Section>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
